import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { createPortal } from 'react-dom';
import { Input, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { IpcButton } from '../components/ui';
import { nodeAutoCompleteRuleApi } from '../api/services';
import type { NodeAutoCompleteRuleStepTestResultVO } from '../api/types/nodeAutoCompleteRule';
import { formatJsonBlock } from '../utils/formatServerDateTime';
import {
  buildDefaultDecisionContextJson,
  buildDefaultPrepareContextJson,
} from '../types/nodeAutoCompleteRuleScriptContext';
import './NodeAutoCompleteScriptFullscreenEditor.css';

const { TextArea } = Input;

export type ScriptEditorStep = 'prepare' | 'decision';

type Props = {
  open: boolean;
  step: ScriptEditorStep;
  script: string;
  timeoutSec: number;
  onClose: () => void;
  /** 保存脚本到数据库成功后回调（父级同步表单） */
  onSaved: (script: string) => void;
  onSave: (script: string) => Promise<void>;
};

const STEP_LABELS: Record<ScriptEditorStep, string> = {
  prepare: '数据准备',
  decision: '动作决策',
};

function buildTestResultText(result: NodeAutoCompleteRuleStepTestResultVO | null, error?: string): string {
  if (error) return error;
  if (!result) return '点击「测试」执行当前脚本';
  const lines: string[] = [];
  lines.push(`success: ${result.success}`);
  lines.push(`duration_ms: ${result.durationMs ?? result.duration_ms ?? 0}`);
  if (result.errorMessage ?? result.error_message) {
    lines.push(`error: ${result.errorMessage ?? result.error_message}`);
  }
  if (result.logs?.length) {
    lines.push('');
    lines.push('[logs]');
    result.logs.forEach(line => lines.push(line));
  }
  const resultJson = result.resultJson ?? result.result_json;
  if (resultJson) {
    lines.push('');
    lines.push('[result]');
    lines.push(formatJsonBlock(resultJson));
  }
  return lines.join('\n');
}

const NodeAutoCompleteScriptFullscreenEditor: FC<Props> = ({
  open,
  step,
  script: initialScript,
  timeoutSec,
  onClose,
  onSaved,
  onSave,
}) => {
  const [script, setScript] = useState(initialScript);
  const [contextJson, setContextJson] = useState('');
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<NodeAutoCompleteRuleStepTestResultVO | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [savedInSession, setSavedInSession] = useState(false);

  const defaultContextJson = useMemo(
    () => (step === 'prepare' ? buildDefaultPrepareContextJson() : buildDefaultDecisionContextJson()),
    [step],
  );

  useEffect(() => {
    if (!open) return;
    setScript(initialScript);
    setContextJson(defaultContextJson);
    setTestResult(null);
    setTestError(null);
    setSavedInSession(false);
  }, [open, initialScript, defaultContextJson]);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const parseContext = useCallback((): unknown => {
    const trimmed = contextJson.trim();
    if (!trimmed) return {};
    try {
      return JSON.parse(trimmed) as unknown;
    } catch {
      throw new Error('context JSON 格式不正确');
    }
  }, [contextJson]);

  const handleTest = async () => {
    if (!script.trim()) {
      message.warning('脚本不能为空');
      return;
    }
    setTesting(true);
    setTestResult(null);
    setTestError(null);
    try {
      const context = parseContext();
      const result = await nodeAutoCompleteRuleApi.testScriptStep({
        step,
        script,
        context,
        timeoutSec,
      });
      setTestResult(result);
    } catch (e) {
      setTestError(e instanceof Error ? e.message : '测试失败');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!script.trim()) {
      message.warning('脚本不能为空');
      return;
    }
    setSaving(true);
    try {
      await onSave(script);
      setSavedInSession(true);
      onSaved(script);
      message.success('脚本已保存');
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!savedInSession && script !== initialScript) {
      // 未保存则丢弃全屏内编辑，父级保持原值
    }
    onClose();
  };

  if (!open) return null;

  const resultText = buildTestResultText(testResult, testError ?? undefined);

  return createPortal(
    <div className="acr-script-fullscreen" role="dialog" aria-modal="true">
      <header className="acr-script-fullscreen__header">
        <div className="acr-script-fullscreen__title">
          {STEP_LABELS[step]} · Jint 脚本编辑
        </div>
        <button
          type="button"
          className="acr-script-fullscreen__close"
          aria-label="退出全屏"
          onClick={handleClose}
        >
          <CloseOutlined />
        </button>
      </header>

      <div className="acr-script-fullscreen__body">
        <section className="acr-script-fullscreen__editor-pane">
          <div className="acr-script-fullscreen__pane-label">脚本</div>
          <TextArea
            className="acr-script-fullscreen__code"
            value={script}
            onChange={e => setScript(e.target.value)}
            spellCheck={false}
            style={{ height: '100%', resize: 'none' }}
          />
        </section>

        <section className="acr-script-fullscreen__bottom-pane">
          <div className="acr-script-fullscreen__context-pane">
            <div className="acr-script-fullscreen__pane-label">context（JSON）</div>
            <TextArea
              className="acr-script-fullscreen__context"
              value={contextJson}
              onChange={e => setContextJson(e.target.value)}
              spellCheck={false}
              style={{ height: '100%', resize: 'none' }}
            />
          </div>
          <div className="acr-script-fullscreen__result-pane">
            <div className="acr-script-fullscreen__pane-label">执行结果</div>
            <pre className={`acr-script-fullscreen__result${testError ? ' is-error' : ''}`}>{resultText}</pre>
          </div>
        </section>
      </div>

      <footer className="acr-script-fullscreen__footer">
        <IpcButton variant="secondary" onClick={handleTest} loading={testing}>
          测试
        </IpcButton>
        <IpcButton variant="primary" onClick={handleSave} loading={saving}>
          保存
        </IpcButton>
      </footer>
    </div>,
    document.body,
  );
};

export default NodeAutoCompleteScriptFullscreenEditor;
